import { Attachment, CompanyService, Item, Listing, Move, MoveLine, Stock, Tag, WarehouseLocation } from '@conveyr/shared-types';
import { AxiosInstance } from 'axios';
import { TransportationCarrierEnum, TransportationCarrierServiceLevelEnum, TransportationProviderEnum } from '@/compiler/transportation';
import { moneyInterface } from '@/compiler/types';
import baseAxios from '@/helpers/forms/axios';
import { CompanyServiceResponse, toCompanyService } from '../companyServices';
import { customerAddressInterface, customerInterface } from '../customers';
import { fulfillmentRequestInterface } from '../fulfillment';
import { ItemResponse, toItem } from '../inventory';
import { StockResponse, toStock } from '../inventory/stocks';
import { orderItemInterface, OrderItemServiceLine } from '../orderItems';
import { packageInterface, PackageServiceLine } from '../packages';
import { TagResponse, toTag } from '../tags';
import { trackingNumberInterface } from '../trackingNumbers';
import labelsFactory from './labels';
import packsFactory from './packs';
import picksFactory from './picks';
import uploadFactory from './upload';

// ── Snake_case Response types (matching raw API responses) ──────────────

export interface OrderQuoteItemResponse {
    carrier_rate: object
    parcels: object[]
}

export interface OrderQuoteResponse {
    id: string
    total: moneyInterface
    carrier: TransportationCarrierEnum
    service_level: TransportationCarrierServiceLevelEnum
    provider: TransportationProviderEnum
    delivery_date: string
    delivery_days: number
    quote_items: OrderQuoteItemResponse[]
}

export interface OrderQuoteSummaryResponse {
    selected_quote_id: string
    cheapest_quote_id: string
    fastest_quote_id: string
    preference_quote_ids?: string[]
    quotes: OrderQuoteResponse[]
    messages: { source: string, text: string }[]
    errors: { source: string, text: string }[]
}

interface CustomerAddressResponse {
    id: number
    customer_id: number
    name: string
    address_line_1: string
    address_line_2: string | null
    address_line_3: string | null
    city: string
    state_province: string | null
    country_code: string
    postal_code: string
    phone_number: string | null
    is_residential: boolean | null
}

interface CustomerResponse {
    id: number
    addresses?: CustomerAddressResponse[]
    created_at: string
    updated_at: string
    name: string
    merchant_id: number
    channel_id: number
    default_address_id: number
    email: string
    phone_number: string
}

interface TrackingNumberResponse {
    id: number
    created_at: string
    updated_at: string
    number: string
    carrier: string
    delivered_at: string
    eta: string
    url: string
    package_identifier: string
    status?: string
}

interface AttachmentResponse {
    id: number
    created_at: string
    updated_at: string
    path: string
    name: string
    url: string
}

export interface PickDataResponse {
    tote_code?: string
    quantity_picked?: number
    scans?: {
        type: string
        code: string
        scanned_at: string
        scanned_by_user_id: number
    }[]
}

interface OrderItemServiceLineResponse {
    order_item_id: number
    service_id: number
    service: CompanyServiceResponse
}

export interface OrderItemResponse {
    id: number
    created_at: string
    updated_at: string
    order_id: number
    order?: OrderResponse
    item_id: number
    item: ItemResponse | null
    listing_id: number
    listing: Listing
    quantity: number
    picked_at?: string
    pick_data?: PickDataResponse
    service_lines?: OrderItemServiceLineResponse[]
    moves: Array<Move<MoveLine<undefined, WarehouseLocation>>>
}

interface BoxResponse {
    id: number
    created_at: string
    updated_at: string
    weight_gm: number
    length_mm: number
    width_mm: number
    height_mm: number
    boxable_id: number
    boxable_type: string
    boxable: object
    weight: number
    length: number
    width: number
    height: number
}

interface PackageItemResponse {
    id: number
    created_at: string
    updated_at: string
    order_item_id: number
    order_item?: OrderItemResponse
    package_id: number
    quantity: number
    tote_barcode: string
    packed_at: string
    packed_by_user_id: number
    lot_code: string
}

interface PackageServiceLineResponse {
    id: number
    package_id: number
    service_id: number
    quantity: number
    service: CompanyServiceResponse
}

interface PackageResponse {
    id: number
    hash: string
    created_at: string
    updated_at: string
    order_id: number
    order?: OrderResponse
    shipped_at: string
    arrived_at: string
    lpn: string
    package_items?: PackageItemResponse[]
    service_lines?: PackageServiceLineResponse[]
    attachments?: AttachmentResponse[]
    box: BoxResponse | null
}

export interface OrderServiceLineResponse {
    id: number
    order_id: number
    service_id: number
    quantity: number
    service: CompanyServiceResponse
}

export interface OrderResponse {
    id: number
    created_at: string
    updated_at: string
    customer_id: number
    customer: CustomerResponse
    shipping_address_id: number
    shipping_address?: CustomerAddressResponse
    billing_address_id: number
    order_id: string
    channel_id: number
    channel_order_id: string
    order_date: string
    ship_by: string
    arrive_by: string
    status: 'waiting' | 'pending' | 'picked' | 'packed' | 'shipped' | 'partially_shipped' | 'delivered' | 'error' | 'cancelled'
    warehouse_notes: string
    gift_note: string | null
    shipping_method_preference: string
    pick_assigned_to: number
    pack_assigned_to: number
    quotes: OrderQuoteSummaryResponse | null
    errors: { source: string, text: string }[] | null
    order_items?: OrderItemResponse[]
    tote_code?: string
    packages?: PackageResponse[]
    attachments?: AttachmentResponse[]
    tracking_numbers?: TrackingNumberResponse[]
    tags?: TagResponse[]
    [key: string]: unknown
}

export interface PackOrderResponse extends OrderResponse {
    packages?: PackageResponse[]
    attachments?: AttachmentResponse[]
    service_lines?: OrderServiceLineResponse[]
    shipping_address: CustomerAddressResponse
}

export interface PickResponse {
    id: number
    item: ItemResponse | null
    orders?: OrderResponse[]
    stocks?: StockResponse[]
}

interface PickSettingsResponse {
    location_scan?: 'optional' | 'required' | 'disabled'
    item_scan?: 'optional' | 'required' | 'required_first_pick' | 'disabled'
}

export interface BatchResponse {
    id: string
    progress: number
    finished_at: string
    failed_jobs: number
}

// ── Consumer-facing types (camelCase, unchanged) ────────────────────────

export interface orderQuoteItemInterface {
    carrierRate: object
    parcels: object[]
}

export interface orderQuoteInterface {
    id: string
    total: moneyInterface
    carrier: TransportationCarrierEnum
    serviceLevel: TransportationCarrierServiceLevelEnum
    provider: TransportationProviderEnum
    deliveryDate: string
    deliveryDays: number
    quoteItems: orderQuoteItemInterface[]
}

export interface orderQuoteSummaryInterface {
    selectedQuoteId: string
    cheapestQuoteId: string
    fastestQuoteId: string
    preferenceQuoteIds?: string[]
    quotes: orderQuoteInterface[]
    messages: { source: string, text: string }[]
    errors: { source: string, text: string }[]
}

export interface orderInterface {
    id: number
    createdAt: string
    updatedAt: string
    customerId: number
    customer: customerInterface
    shippingAddressId: number
    shippingAddress?: customerAddressInterface
    billingAddressId: number
    orderId: string
    channelId: number
    channelOrderId: string
    orderDate: string
    shipBy: string | null
    arriveBy: string | null
    status: 'waiting' | 'pending' | 'picked' | 'packed' | 'shipped' | 'partially_shipped' | 'delivered' | 'error' | 'cancelled'
    warehouseNotes: string
    giftNote: string
    shippingMethodPreference: string
    pickAssignedTo: number
    packAssignedTo: number
    quotes: orderQuoteSummaryInterface | null
    errors: { source: string, text: string }[] | null
    orderItems: orderItemInterface[]
    toteCode?: string
    packages?: packageInterface[]
    attachments?: Attachment[]
    trackingNumbers?: trackingNumberInterface[]
    tags: Tag[]
};

export interface packOrderInterface extends orderInterface {
    packages: packageInterface[]
    attachments: Attachment[]
    serviceLines: Array<OrderServiceLine<CompanyService>>
    shippingAddress: customerAddressInterface
}

export interface OrderServiceLine<
    S extends CompanyService | undefined = any, // eslint-disable-line @typescript-eslint/no-explicit-any -- required for legacy compatibility; fixing would require broader type refactoring
> {
    id: number
    orderId: number
    serviceId: number
    quantity: number
    service: S
}

export interface pickDataInterface {
    toteCode?: string
    quantityPicked?: number
    scans?: {
        type: string
        code: string
        scannedAt: string
        scannedByUserId: number
    }[]
}

export interface pickInterface {
    id: number
    item: Item | null
    orders: orderInterface[]
    stocks: Array<Stock<WarehouseLocation>>
}

export interface pickSettingsInterface {
    locationScan?: 'optional' | 'required' | 'disabled'
    itemScan?: 'optional' | 'required' | 'required_first_pick' | 'disabled'
}

export interface batchResponseInterface {
    id: string
    progress: number
    finishedAt: string
    failedJobs: number
}

interface pickResponseInterface {
    picks: pickInterface[]
    pickSettings: pickSettingsInterface
}

interface orderResponseInterface {
    order: orderInterface
    fulfillmentRequests: fulfillmentRequestInterface[]
}

// ── Converter functions (snake_case Response → camelCase interface) ─────

function toCustomerAddress(r: CustomerAddressResponse): customerAddressInterface {
    return {
        id: r.id,
        customerId: r.customer_id,
        name: r.name,
        addressLine1: r.address_line_1,
        addressLine2: r.address_line_2,
        addressLine3: r.address_line_3,
        city: r.city,
        stateProvince: r.state_province,
        countryCode: r.country_code,
        postalCode: r.postal_code,
        phoneNumber: r.phone_number,
        isResidential: r.is_residential,
    };
}

function toCustomer(r: CustomerResponse): customerInterface {
    return {
        id: r.id,
        addresses: r.addresses?.map(toCustomerAddress) ?? [],
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        name: r.name,
        merchantId: r.merchant_id,
        channelId: r.channel_id,
        defaultAddressId: r.default_address_id,
        email: r.email,
        phoneNumber: r.phone_number,
    };
}

function toTrackingNumber(r: TrackingNumberResponse): trackingNumberInterface {
    return {
        id: r.id,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        number: r.number,
        carrier: r.carrier,
        deliveredAt: r.delivered_at,
        eta: r.eta,
        url: r.url,
        packageIdentifier: r.package_identifier,
        status: r.status,
    };
}

function toAttachment(r: AttachmentResponse): Attachment {
    return {
        id: r.id,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        path: r.path,
        name: r.name,
        url: r.url,
    };
}

function toOrderQuoteItem(r: OrderQuoteItemResponse): orderQuoteItemInterface {
    return {
        carrierRate: r.carrier_rate,
        parcels: r.parcels,
    };
}

function toOrderQuote(r: OrderQuoteResponse): orderQuoteInterface {
    return {
        id: r.id,
        total: r.total,
        carrier: r.carrier,
        serviceLevel: r.service_level,
        provider: r.provider,
        deliveryDate: r.delivery_date,
        deliveryDays: r.delivery_days,
        quoteItems: r.quote_items.map(toOrderQuoteItem),
    };
}

export function toOrderQuoteSummary(r: OrderQuoteSummaryResponse): orderQuoteSummaryInterface;
export function toOrderQuoteSummary(r: OrderQuoteSummaryResponse | null): orderQuoteSummaryInterface | null;
export function toOrderQuoteSummary(r: OrderQuoteSummaryResponse | null): orderQuoteSummaryInterface | null {
    if (!r) return null;
    return {
        selectedQuoteId: r.selected_quote_id,
        cheapestQuoteId: r.cheapest_quote_id,
        fastestQuoteId: r.fastest_quote_id,
        preferenceQuoteIds: r.preference_quote_ids,
        quotes: r.quotes.map(toOrderQuote),
        messages: r.messages,
        errors: r.errors,
    };
}

export function toPickData(r: PickDataResponse): pickDataInterface;
export function toPickData(r: PickDataResponse | undefined): pickDataInterface | undefined;
export function toPickData(r: PickDataResponse | undefined): pickDataInterface | undefined {
    if (!r) return undefined;
    return {
        toteCode: r.tote_code,
        quantityPicked: r.quantity_picked,
        scans: r.scans?.map(s => ({
            type: s.type,
            code: s.code,
            scannedAt: s.scanned_at,
            scannedByUserId: s.scanned_by_user_id,
        })),
    };
}

function toOrderItemServiceLine(r: OrderItemServiceLineResponse): OrderItemServiceLine<CompanyService> {
    return {
        orderItemId: r.order_item_id,
        serviceId: r.service_id,
        service: toCompanyService(r.service),
    };
}

function toOrderItem(r: OrderItemResponse): orderItemInterface {
    return {
        id: r.id,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        orderId: r.order_id,
        itemId: r.item_id,
        item: toItem(r.item),
        listingId: r.listing_id,
        listing: r.listing,
        quantity: r.quantity,
        pickedAt: r.picked_at,
        pickData: toPickData(r.pick_data),
        serviceLines: r.service_lines?.map(toOrderItemServiceLine) ?? [],
        moves: r.moves,
    };
}

function toBox(r: BoxResponse): packageInterface['box'] {
    return {
        id: r.id,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        weightGm: r.weight_gm,
        lengthMm: r.length_mm,
        widthMm: r.width_mm,
        heightMm: r.height_mm,
        boxableId: r.boxable_id,
        boxableType: r.boxable_type,
        boxable: r.boxable,
        weight: r.weight,
        length: r.length,
        width: r.width,
        height: r.height,
    };
}

function toPackageItem(r: PackageItemResponse): packageInterface['packageItems'][number] {
    return {
        id: r.id,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        orderItemId: r.order_item_id,
        orderItem: r.order_item ? toOrderItem(r.order_item) : undefined,
        packageId: r.package_id,
        quantity: r.quantity,
        toteBarcode: r.tote_barcode,
        packedAt: r.packed_at,
        packedByUserId: r.packed_by_user_id,
        lotCode: r.lot_code,
    };
}

function toPackageServiceLine(r: PackageServiceLineResponse): PackageServiceLine<CompanyService> {
    return {
        id: r.id,
        packageId: r.package_id,
        serviceId: r.service_id,
        quantity: r.quantity,
        service: toCompanyService(r.service),
    };
}

function toPackage(r: PackageResponse): packageInterface {
    return {
        id: r.id,
        hash: r.hash,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        orderId: r.order_id,
        shippedAt: r.shipped_at,
        arrivedAt: r.arrived_at,
        lpn: r.lpn,
        packageItems: r.package_items?.map(toPackageItem) ?? [],
        serviceLines: r.service_lines?.map(toPackageServiceLine) ?? [],
        attachments: r.attachments?.map(toAttachment) ?? [],
        box: r.box ? toBox(r.box) : null,
    };
}

export function toOrderServiceLine(r: OrderServiceLineResponse): OrderServiceLine<CompanyService> {
    return {
        id: r.id,
        orderId: r.order_id,
        serviceId: r.service_id,
        quantity: r.quantity,
        service: toCompanyService(r.service),
    };
}

export function toOrder(r: OrderResponse): orderInterface {
    return {
        id: r.id,
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        customerId: r.customer_id,
        customer: toCustomer(r.customer),
        shippingAddressId: r.shipping_address_id,
        shippingAddress: r.shipping_address ? toCustomerAddress(r.shipping_address) : undefined,
        billingAddressId: r.billing_address_id,
        orderId: r.order_id,
        channelId: r.channel_id,
        channelOrderId: r.channel_order_id,
        orderDate: r.order_date,
        shipBy: r.ship_by,
        arriveBy: r.arrive_by,
        status: r.status,
        warehouseNotes: r.warehouse_notes,
        giftNote: r.gift_note ?? '',
        shippingMethodPreference: r.shipping_method_preference,
        pickAssignedTo: r.pick_assigned_to,
        packAssignedTo: r.pack_assigned_to,
        quotes: toOrderQuoteSummary(r.quotes),
        errors: r.errors,
        orderItems: r.order_items?.map(toOrderItem) ?? [],
        toteCode: r.tote_code,
        packages: r.packages?.map(toPackage),
        attachments: r.attachments?.map(toAttachment),
        trackingNumbers: r.tracking_numbers?.map(toTrackingNumber),
        tags: r.tags?.map(toTag) ?? [],
    };
}

export function toPackOrder(r: PackOrderResponse): packOrderInterface {
    return {
        ...toOrder(r),
        packages: r.packages?.map(toPackage) ?? [],
        attachments: r.attachments?.map(toAttachment) ?? [],
        serviceLines: r.service_lines?.map(toOrderServiceLine) ?? [],
        shippingAddress: toCustomerAddress(r.shipping_address),
    };
}

export function toPick(r: PickResponse): pickInterface {
    return {
        id: r.id,
        item: toItem(r.item),
        orders: r.orders?.map(toOrder) ?? [],
        stocks: r.stocks?.map(toStock) ?? [],
    };
}

function toPickSettings(r: PickSettingsResponse): pickSettingsInterface {
    return {
        locationScan: r.location_scan,
        itemScan: r.item_scan,
    };
}

export function toBatchResponse(r: BatchResponse): batchResponseInterface {
    return {
        id: r.id,
        progress: r.progress,
        finishedAt: r.finished_at,
        failedJobs: r.failed_jobs,
    };
}

// ── API factory ─────────────────────────────────────────────────────────

interface OrdersApiReturn {
    labels: ReturnType<typeof labelsFactory>
    packs: ReturnType<typeof packsFactory>
    picks: ReturnType<typeof picksFactory>
    upload: ReturnType<typeof uploadFactory>
    fetchOrder: (orderId: number) => Promise<orderResponseInterface>
    updateOrder: (orderId: number, payload: UpdateOrderPayload) => Promise<void>
    fetchFulfillableOrders: () => Promise<orderInterface[]>
    fetchAllOrders: (filters?: { [key: string]: string | number }) => Promise<orderInterface[]>
    fetchPackOrders: () => Promise<packOrderInterface[]>
    fetchPickOrders: () => Promise<pickResponseInterface>
    deleteOrders: (ids: number[]) => Promise<null>
    untagOrder: (orderId: number, tagId: number) => Promise<void>
    tagOrder: (orderId: number, tagId: number) => Promise<void>
    updateServicesForOrder: (orderId: number, services: Array<{ id: number, quantity: number }>) => Promise<OrderServiceLine[]>
    updateServicesForOrderItem: (orderItemId: number, services: Array<{ id: number }>) => Promise<OrderItemServiceLine[]>
    updateServicesForPackage: (packageId: number, services: Array<{ id: number, quantity: number }>) => Promise<PackageServiceLine[]>
}

export interface UpdateOrderPayload {
    customer: { id: number; name: string }
    shippingAddress: { id: number; isResidential: boolean; countryCode: string }
    billingAddress: { id: number; isResidential: boolean; countryCode: string }
    orderId: string
    shipBy: string
    arriveBy: string
    warehouseNotes: string
    giftNote: string
    items: Array<{
        id: number
        title: string
        merchantSku: string
        quantity: number
    }>
}

export default (axios: AxiosInstance = baseAxios): OrdersApiReturn => ({
    // Nested sub-modules
    labels: labelsFactory(axios),
    packs: packsFactory(axios),
    picks: picksFactory(axios),
    upload: uploadFactory(axios),

    // Direct methods from repositories/orders.ts
    fetchOrder: async (orderId: number): Promise<orderResponseInterface> => {
        const response = await axios.get<{ order: OrderResponse, fulfillment_requests?: fulfillmentRequestInterface[] }>(`/api/orders/${orderId}`);

        return {
            order: toOrder(response.data.order),
            fulfillmentRequests: response.data.fulfillment_requests ?? [],
        };
    },

    updateOrder: async (orderId: number, payload: UpdateOrderPayload): Promise<void> => {
        const requestPayload: Record<string, unknown> = {
            customer: {
                id: payload.customer.id,
                name: payload.customer.name,
            },
            shipping_address: {
                id: payload.shippingAddress.id,
                is_residential: payload.shippingAddress.isResidential,
                country_code: payload.shippingAddress.countryCode,
            },
            billing_address: {
                id: payload.billingAddress.id,
                is_residential: payload.billingAddress.isResidential,
                country_code: payload.billingAddress.countryCode,
            },
            order_id: payload.orderId,
            ship_by: payload.shipBy,
            arrive_by: payload.arriveBy,
            warehouse_notes: payload.warehouseNotes,
            items: payload.items.map(item => ({
                id: item.id,
                title: item.title,
                merchant_sku: item.merchantSku,
                quantity: item.quantity,
            })),
        };

        if (payload.giftNote !== '') {
            requestPayload.gift_note = payload.giftNote;
        }

        await axios.put(`/api/orders/${orderId}`, requestPayload);
    },

    fetchFulfillableOrders: async (): Promise<orderInterface[]> => {
        const response = await axios.get<{ orders: OrderResponse[] }>('/api/orders?fulfillable=1');
        return response.data.orders.map(toOrder);
    },

    fetchAllOrders: async (filters?: { [key: string]: string | number }): Promise<orderInterface[]> => {
        const uri = new URL('/api/orders', window.location.origin);
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                uri.searchParams.append(`filters[${key}]`, value.toString());
            });
        }

        const response = await axios.get<{ orders: OrderResponse[] }>(uri.toString());

        return response.data.orders.map(toOrder);
    },

    fetchPackOrders: async (): Promise<packOrderInterface[]> => {
        const response = await axios.get<{ orders: PackOrderResponse[] }>('/api/orders/packs');
        return response.data.orders.map(toPackOrder);
    },

    fetchPickOrders: async (): Promise<pickResponseInterface> => {
        const response = await axios.get<{ picks: PickResponse[], pick_settings: PickSettingsResponse }>('/api/orders/picks');

        return {
            picks: response.data.picks.map(toPick),
            pickSettings: toPickSettings(response.data.pick_settings),
        };
    },

    deleteOrders: async (ids: number[]): Promise<null> => {
        await axios.post('/api/orders/batch/delete', { ids });

        return null;
    },

    untagOrder: async (orderId: number, tagId: number): Promise<void> => {
        return await axios.delete(`/api/orders/${orderId}/tags/${tagId}`);
    },

    tagOrder: async (orderId: number, tagId: number): Promise<void> => {
        return await axios.post(`/api/orders/${orderId}/tags/${tagId}`);
    },

    updateServicesForOrder: async (orderId: number, services: Array<{ id: number, quantity: number }>): Promise<OrderServiceLine[]> => {
        const response = await axios.post<{ service_lines: OrderServiceLineResponse[] }>(`/api/orders/${orderId}/services`, { services });

        return response.data.service_lines.map(toOrderServiceLine);
    },

    updateServicesForOrderItem: async (orderItemId: number, services: Array<{ id: number }>): Promise<OrderItemServiceLine[]> => {
        const response = await axios.post<{ service_lines: OrderItemServiceLineResponse[] }>(`/api/order-items/${orderItemId}/services`, { services });

        return response.data.service_lines.map(toOrderItemServiceLine);
    },

    updateServicesForPackage: async (packageId: number, services: Array<{ id: number, quantity: number }>): Promise<PackageServiceLine[]> => {
        const response = await axios.post<{ service_lines: PackageServiceLineResponse[] }>(`/api/packages/${packageId}/services`, { services });

        return response.data.service_lines.map(toPackageServiceLine);
    },
});
